import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Trash2, TrendingUp, TrendingDown, Bell, BellOff } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import OccupationLink from "@/components/shared/OccupationLink";
import { toTitleCase } from "@/lib/utils";

interface WatchlistItem {
  id: string;
  occupation_code: string;
  occupation_title: string;
  area_code: string;
  area_name: string;
  created_at: string;
}

interface NotificationSetting {
  id: string;
  occupation_code: string;
  threshold_type: string;
  threshold_value: number;
  is_active: boolean;
}

const WatchlistManager = () => {
  const [watchlist, setWatchlist] = useState<WatchlistItem[]>([]);
  const [notifications, setNotifications] = useState<NotificationSetting[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      fetchWatchlistData();
    }
  }, [user]);

  const fetchWatchlistData = async () => {
    try {
      const [watchlistResponse, notificationsResponse] = await Promise.all([
        supabase
          .from('user_watchlists')
          .select('*')
          .order('created_at', { ascending: false }),
        supabase
          .from('trend_notifications')
          .select('*')
          .eq('is_active', true)
      ]);

      if (watchlistResponse.error) throw watchlistResponse.error;
      if (notificationsResponse.error) throw notificationsResponse.error;

      setWatchlist(watchlistResponse.data || []);
      setNotifications(notificationsResponse.data || []);
    } catch (error) {
      console.error('Error fetching watchlist:', error);
      toast.error("Failed to load watchlist");
    } finally {
      setLoading(false);
    }
  };

  const removeFromWatchlist = async (id: string) => {
    try {
      const { error } = await supabase
        .from('user_watchlists')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setWatchlist(prev => prev.filter(item => item.id !== id));
      toast.success("Removed from watchlist");
    } catch (error) {
      console.error('Error removing from watchlist:', error);
      toast.error("Failed to remove from watchlist");
    }
  };

  const toggleNotification = async (occupationCode: string, isActive: boolean) => {
    try {
      if (isActive) {
        const { error } = await supabase
          .from('trend_notifications')
          .update({ is_active: false })
          .eq('occupation_code', occupationCode)
          .eq('user_id', user?.id);

        if (error) throw error;
        toast.success("Notifications disabled");
      } else {
        const { error } = await supabase
          .from('trend_notifications')
          .insert({
            user_id: user?.id,
            occupation_code: occupationCode,
            area_code: '99', // National
            notification_type: 'salary_change',
            threshold_type: 'percentage',
            threshold_value: 5.0, // 5% change threshold
            is_active: true
          });

        if (error) throw error;
        toast.success("Notifications enabled");
      }

      fetchWatchlistData();
    } catch (error) {
      console.error('Error toggling notification:', error);
      toast.error("Failed to update notifications");
    }
  };

  const hasNotification = (occupationCode: string) => {
    return notifications.some(n => n.occupation_code === occupationCode);
  };

  // Public sign-in is hidden; watchlist is account-only legacy UI — omit for anonymous visitors.
  if (!user) {
    return null;
  }

  if (loading) {
    return (
      <Card className="shadow-lg">
        <CardHeader>
          <CardTitle>Your Watchlist</CardTitle>
          <CardDescription>Loading your tracked occupations...</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card className="shadow-lg">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TrendingUp className="w-5 h-5" />
          Your Watchlist
        </CardTitle>
        <CardDescription>
          Track salary trends for occupations you're interested in
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {watchlist.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <TrendingDown className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No occupations in your watchlist yet</p>
            <p className="text-sm">Use the search above to add occupations to track</p>
          </div>
        ) : (
          watchlist.map((item) => (
            <div
              key={item.id}
              className="flex items-center justify-between p-4 border rounded-lg hover:bg-accent/50 transition-colors"
            >
              <div className="flex-1">
                <h4 className="font-medium">
                  <OccupationLink 
                    occupation={{ 
                      occ_title: item.occupation_title, 
                      soc: item.occupation_code 
                    }}
                    location={item.area_name === 'United States' ? 'united-states' : item.area_name}
                    className="hover:text-primary/80"
                  >
                    {toTitleCase(item.occupation_title)}
                  </OccupationLink>
                </h4>
                <p className="text-sm text-muted-foreground">
                  {item.area_name} • Added {new Date(item.created_at).toLocaleDateString()}
                </p>
                <div className="flex items-center gap-2 mt-2">
                  <Badge variant="outline" className="text-xs">
                    {item.occupation_code}
                  </Badge>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => toggleNotification(
                    item.occupation_code,
                    hasNotification(item.occupation_code)
                  )}
                >
                  {hasNotification(item.occupation_code) ? (
                    <BellOff className="w-4 h-4" />
                  ) : (
                    <Bell className="w-4 h-4" />
                  )}
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => removeFromWatchlist(item.id)}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
};

export default WatchlistManager;
